import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, FileText, Check, RotateCcw, AlertTriangle, X, BookOpen } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';
import { baseURL } from '@/config/APIConfig';

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';
const API_URL = baseURL.AiBaseURL;

const JOURNAL_TYPES = [
    { value: 'manual', label: 'Manual Entry' },
    { value: 'adjusting', label: 'Adjusting Entry' },
    { value: 'closing', label: 'Closing Entry' },
];

const JournalEntries = () => {
    const { t } = useLanguage();
    const [journals, setJournals] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterPosted, setFilterPosted] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingJournal, setEditingJournal] = useState(null);
    
    const [formData, setFormData] = useState({
        journal_date: new Date().toISOString().split('T')[0],
        journal_type: 'manual',
        description: '',
        lines: []
    });

    useEffect(() => {
        fetchJournals();
        fetchAccounts();
    }, [filterType, filterPosted]);

    const fetchJournals = async () => {
        try {
            let url = `${API_URL}/api/accounting/journals?`;
            if (filterType) url += `journal_type=${filterType}&`;
            if (filterPosted !== '') url += `is_posted=${filterPosted}&`;
            
            const response = await fetch(url);
            const data = await response.json();
            setJournals(data);
        } catch (error) {
            toast.error('Failed to load journal entries');
        } finally {
            setLoading(false);
        }
    };

    const fetchAccounts = async () => {
        try {
            const response = await fetch(`${API_URL}/api/accounting/chart-of-accounts?posting_allowed=true`);
            const data = await response.json();
            setAccounts(data);
        } catch (error) {
            console.error('Error fetching accounts:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate debits = credits
        const totalDebit = formData.lines.reduce((sum, line) => sum + (parseFloat(line.debit_amount) || 0), 0);
        const totalCredit = formData.lines.reduce((sum, line) => sum + (parseFloat(line.credit_amount) || 0), 0);
        
        if (Math.abs(totalDebit - totalCredit) > 0.01) {
            toast.error('Journal entry must balance. Total debits must equal total credits.');
            return;
        }
        
        if (formData.lines.length < 2) {
            toast.error('Journal entry must have at least 2 lines.');
            return;
        }
        
        try {
            const url = editingJournal 
                ? `${API_URL}/api/accounting/journals/${editingJournal.id}`
                : `${API_URL}/api/accounting/journals`;
            
            const response = await fetch(url, {
                method: editingJournal ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to save journal entry');
            }
            
            toast.success(editingJournal ? 'Journal entry updated!' : 'Journal entry created!');
            setShowModal(false);
            setEditingJournal(null);
            resetForm();
            fetchJournals();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handlePost = async (id) => {
        try {
            const response = await fetch(`${API_URL}/api/accounting/journals/${id}/post`, { method: 'POST' });
            if (!response.ok) throw new Error('Failed to post');
            toast.success('Journal entry posted!');
            fetchJournals();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleReverse = async (id) => {
        if (!window.confirm('Create a reversal entry for this journal?')) return;
        try {
            const response = await fetch(`${API_URL}/api/accounting/journals/${id}/reverse`, { method: 'POST' });
            if (!response.ok) throw new Error('Failed to reverse');
            toast.success('Journal entry reversed!');
            fetchJournals();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this journal entry?')) return;
        try {
            const response = await fetch(`${API_URL}/api/accounting/journals/${id}`, { method: 'DELETE' });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to delete');
            }
            toast.success('Journal entry deleted!');
            fetchJournals();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const openEditModal = async (journal) => {
        const response = await fetch(`${API_URL}/api/accounting/journals/${journal.id}`);
        const data = await response.json();
        setEditingJournal(data);
        setFormData({
            journal_date: data.journal_date || '',
            journal_type: data.journal_type || 'manual',
            description: data.description || '',
            lines: data.lines || []
        });
        setShowModal(true);
    };

    const resetForm = () => {
        setFormData({
            journal_date: new Date().toISOString().split('T')[0],
            journal_type: 'manual',
            description: '',
            lines: []
        });
    };

    const addLine = () => {
        setFormData({
            ...formData,
            lines: [...formData.lines, { 
                line_no: formData.lines.length + 1, 
                account_id: '', 
                description: '', 
                debit_amount: 0, 
                credit_amount: 0 
            }]
        });
    };

    const updateLine = (index, field, value) => {
        const newLines = [...formData.lines];
        newLines[index] = { ...newLines[index], [field]: value };
        
        // Clear the opposite field when entering a value
        if (field === 'debit_amount' && parseFloat(value) > 0) {
            newLines[index].credit_amount = 0;
        } else if (field === 'credit_amount' && parseFloat(value) > 0) {
            newLines[index].debit_amount = 0;
        }
        
        setFormData({ ...formData, lines: newLines });
    };

    const removeLine = (index) => {
        setFormData({ ...formData, lines: formData.lines.filter((_, i) => i !== index) });
    };

    const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
    const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

    const getAccountName = (accountId) => {
        const account = accounts.find(a => a.id === accountId);
        return account ? `${account.account_code} - ${account.account_name}` : '-';
    };

    const totalDebit = formData.lines.reduce((sum, l) => sum + (parseFloat(l.debit_amount) || 0), 0);
    const totalCredit = formData.lines.reduce((sum, l) => sum + (parseFloat(l.credit_amount) || 0), 0);
    const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

    return (
        <div className="p-6 space-y-6 bg-slate-50 dark:bg-slate-900 min-h-screen">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-800 via-indigo-700 to-purple-700 rounded-2xl p-6 text-white">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                            <BookOpen className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">{t('journalEntries')}</h1>
                            <p className="text-indigo-200">{t('manageJournals')}</p>
                        </div>
                    </div>
                    <Button 
                        onClick={() => { resetForm(); setEditingJournal(null); setShowModal(true); }}
                        className="bg-white text-indigo-700 hover:bg-indigo-50"
                        data-testid="new-journal-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" /> {t('newJournal')}
                    </Button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 border-l-indigo-500">
                    <p className="text-sm text-slate-500">Total Entries</p>
                    <p className="text-2xl font-bold">{journals.length}</p>
                </div>
                <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 border-l-amber-500">
                    <p className="text-sm text-slate-500">Unposted</p>
                    <p className="text-2xl font-bold">{journals.filter(j => !j.is_posted).length}</p>
                </div>
                <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 border-l-green-500">
                    <p className="text-sm text-slate-500">Posted</p>
                    <p className="text-2xl font-bold">{journals.filter(j => j.is_posted).length}</p>
                </div>
                <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 border-l-red-500">
                    <p className="text-sm text-slate-500">Reversed</p>
                    <p className="text-2xl font-bold">{journals.filter(j => j.reversed).length}</p>
                </div>
            </div>

            {/* Filters */}
            <div className="flex gap-4 items-center bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm">
                <div className="flex-1 max-w-md">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
                    </div>
                </div>
                <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800">
                    <option value="">All Types</option>
                    {JOURNAL_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                </select>
                <select value={filterPosted} onChange={(e) => setFilterPosted(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800">
                    <option value="">All Status</option>
                    <option value="false">Unposted</option>
                    <option value="true">Posted</option>
                </select>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="bg-slate-100 dark:bg-slate-700">
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase">JV #</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Type</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Description</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase">Debit</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase">Credit</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Status</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                        {loading ? (
                            <tr><td colSpan={8} className="px-4 py-8 text-center">{t('loading')}</td></tr>
                        ) : journals.length === 0 ? (
                            <tr><td colSpan={8} className="px-4 py-8 text-center">{t('noJournalsFound')}</td></tr>
                        ) : journals.filter(j => 
                            !searchTerm || 
                            j.journal_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            j.description?.toLowerCase().includes(searchTerm.toLowerCase())
                        ).map((journal) => (
                            <tr key={journal.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                                <td className="px-4 py-3 text-sm font-medium text-indigo-600">{journal.journal_no}</td>
                                <td className="px-4 py-3 text-sm">{formatDate(journal.journal_date)}</td>
                                <td className="px-4 py-3 text-sm capitalize">{journal.journal_type}</td>
                                <td className="px-4 py-3 text-sm truncate max-w-xs">{journal.description || '-'}</td>
                                <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(journal.total_debit)}</td>
                                <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(journal.total_credit)}</td>
                                <td className="px-4 py-3 text-center">
                                    {journal.reversed ? (
                                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Reversed</span>
                                    ) : journal.is_posted ? (
                                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Posted</span>
                                    ) : (
                                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Draft</span>
                                    )}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center justify-center gap-1">
                                        {!journal.is_posted && !journal.reversed && (
                                            <>
                                                <button onClick={() => openEditModal(journal)} className="p-1.5 rounded-lg hover:bg-blue-100 text-blue-600"><Edit2 className="w-4 h-4" /></button>
                                                <button onClick={() => handlePost(journal.id)} className="p-1.5 rounded-lg hover:bg-green-100 text-green-600" title="Post"><Check className="w-4 h-4" /></button>
                                                <button onClick={() => handleDelete(journal.id)} className="p-1.5 rounded-lg hover:bg-red-100 text-red-600"><Trash2 className="w-4 h-4" /></button>
                                            </>
                                        )}
                                        {journal.is_posted && !journal.reversed && (
                                            <button onClick={() => handleReverse(journal.id)} className="p-1.5 rounded-lg hover:bg-amber-100 text-amber-600" title="Reverse"><RotateCcw className="w-4 h-4" /></button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b flex items-center justify-between">
                            <h2 className="text-xl font-bold">{editingJournal ? `${t('editJournal')} ${editingJournal.journal_no}` : t('newJournal')}</h2>
                            <button onClick={() => { setShowModal(false); setEditingJournal(null); }} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-6">
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Journal Date *</label>
                                    <Input type="date" value={formData.journal_date} onChange={(e) => setFormData({...formData, journal_date: e.target.value})} required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Type *</label>
                                    <select value={formData.journal_type} onChange={(e) => setFormData({...formData, journal_type: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800">
                                        {JOURNAL_TYPES.map(type => (
                                            <option key={type.value} value={type.value}>{type.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Description</label>
                                    <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Journal description..." />
                                </div>
                            </div>

                            {/* Lines */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-sm font-semibold">{t('addLine')}</h3>
                                    <Button type="button" onClick={addLine} size="sm" variant="outline"><Plus className="w-4 h-4 mr-1" /> {t('addLine')}</Button>
                                </div>
                                
                                <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 space-y-3">
                                    <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-slate-500 pb-2 border-b">
                                        <div className="col-span-5">Account</div>
                                        <div className="col-span-3">Description</div>
                                        <div className="col-span-2 text-right">Debit</div>
                                        <div className="col-span-2 text-right">Credit</div>
                                    </div>
                                    
                                    {formData.lines.map((line, index) => (
                                        <div key={index} className="grid grid-cols-12 gap-2 items-center">
                                            <div className="col-span-5">
                                                <select
                                                    value={line.account_id}
                                                    onChange={(e) => updateLine(index, 'account_id', e.target.value)}
                                                    className="w-full px-2 py-1.5 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
                                                    required
                                                >
                                                    <option value="">Select Account...</option>
                                                    {accounts.filter(a => a.posting_allowed !== false).map(acc => (
                                                        <option key={acc.id} value={acc.id}>{acc.account_code} - {acc.account_name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-span-3">
                                                <Input value={line.description} onChange={(e) => updateLine(index, 'description', e.target.value)} placeholder="Line description" className="py-1.5" />
                                            </div>
                                            <div className="col-span-2">
                                                <Input 
                                                    type="number" 
                                                    step="0.01" 
                                                    min="0"
                                                    value={line.debit_amount || ''} 
                                                    onChange={(e) => updateLine(index, 'debit_amount', e.target.value)} 
                                                    placeholder="0.00"
                                                    className="py-1.5 text-right"
                                                />
                                            </div>
                                            <div className="col-span-2 flex items-center gap-1">
                                                <Input 
                                                    type="number" 
                                                    step="0.01" 
                                                    min="0"
                                                    value={line.credit_amount || ''} 
                                                    onChange={(e) => updateLine(index, 'credit_amount', e.target.value)} 
                                                    placeholder="0.00"
                                                    className="py-1.5 text-right"
                                                />
                                                <button type="button" onClick={() => removeLine(index)} className="p-1 text-red-500 hover:bg-red-100 rounded">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    
                                    {formData.lines.length === 0 && (
                                        <div className="text-center py-8 text-slate-500">
                                            No lines. Click "Add Line" to start.
                                        </div>
                                    )}
                                </div>
                                
                                {/* Totals */}
                                {formData.lines.length > 0 && (
                                    <div className="flex justify-end">
                                        <div className="w-80 bg-slate-100 dark:bg-slate-900 rounded-lg p-4 space-y-2">
                                            <div className="flex justify-between text-sm">
                                                <span>{t('totalDebit')}:</span>
                                                <span className="font-semibold">{formatCurrency(totalDebit)}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span>{t('totalCredit')}:</span>
                                                <span className="font-semibold">{formatCurrency(totalCredit)}</span>
                                            </div>
                                            <div className="flex justify-between text-sm border-t pt-2">
                                                <span>Difference:</span>
                                                <span className={`font-bold ${isBalanced ? 'text-green-600' : 'text-red-600'}`}>
                                                    {formatCurrency(Math.abs(totalDebit - totalCredit))}
                                                </span>
                                            </div>
                                            {!isBalanced && (
                                                <div className="flex items-center gap-2 text-red-600 text-xs">
                                                    <AlertTriangle className="w-4 h-4" />
                                                    <span>Entry must balance to save</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <Button type="button" variant="outline" onClick={() => { setShowModal(false); setEditingJournal(null); }}>{t('cancel')}</Button>
                                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={!isBalanced || formData.lines.length < 2}>
                                    {editingJournal ? t('update') : t('create')}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JournalEntries;
